import { Button, Flex, Space, Spacing, Text, useModal } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EInvoicingCheckmark, EInvoicingContract, EInvoicingThumbsUp } from '../../../../assets/images'
import { ModalId } from '../../../../enums/modalId'
import { useSegment } from '../../../../hooks'
import { useEmberTransitionTo } from '../../../../hooks/routing/useEmberTransitionTo'
import { E_INVOICING_STEP_TO_PATH, E_INVOICING_SUPPORT_LINK } from '../../../eInvoicing/constants'
import { EInvoicingKYCRefused } from '../../../eInvoicing/elements/EInvoicingKYCRefused'
import { useEInvoicingSetup } from '../../../eInvoicing/hooks/useEInvoicingSetup'
import { getEInvoicingBaseUrl } from '../../../eInvoicing/utils'
import { EInvoicingFooter } from '../EInvoicingFooter'
import { EInvoicingGenericStatus } from '../EInvoicingGenericStatus'
import { EInvoicingGetStartedStep } from './elements/EInvoicingGetStartedStep'
import * as Styled from './styles'

export interface EInvoicingGetStartedProps {
  proceedToNextStep?: boolean
}

export const EInvoicingGetStarted = ({
  proceedToNextStep,
}: EInvoicingGetStartedProps): ReactElement<EInvoicingGetStartedProps> => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { transitionTo } = useEmberTransitionTo()
  const { close } = useModal(ModalId.AttachmentsModal)
  const { goToNextStep, kycStatus } = useEInvoicingSetup()

  const handleGetStartedClick = useCallback(() => {
    close()
    // If we come from a step we should just go to the next step
    if (proceedToNextStep) {
      goToNextStep()
    } else {
      // Else it means this component is used outside of the flow so we should navigate to the beginning of the flow
      const url = `${getEInvoicingBaseUrl(organization)}/${E_INVOICING_STEP_TO_PATH.COMPANY_INFO}`
      transitionTo(url)
    }
    track('xxx - e-invoicing: Clicked "Get Started" (Client)')
  }, [close, proceedToNextStep, goToNextStep, organization, track, transitionTo])

  if (kycStatus === 'KYC_REJECTED') {
    return <EInvoicingKYCRefused />
  }

  return (
    <EInvoicingGenericStatus title={t('e_invoice.kyc.step.get_started.title')} fullWidth={true}>
      <Styled.GetStartedContainer>
        <Flex flexDirection="column" alignItems="center" justifyContent="center" width="100%">
          <Text alignment="center">{t('e_invoice.kyc.step.get_started.subtitle')}</Text>
          <Space size="s32" />
          <Button onClick={handleGetStartedClick}>{t('e_invoice.kyc.step.get_started.cta.primary')}</Button>
          <Space size="s40" />
          <Flex justifyContent="center" alignItems="center" gap={Spacing.S40} width="100%">
            <EInvoicingGetStartedStep
              title={t('e_invoice.kyc.step.get_started.step1.title')}
              description={t('e_invoice.kyc.step.get_started.step1.description')}
              illustration={EInvoicingThumbsUp}
            />
            <EInvoicingGetStartedStep
              title={t('e_invoice.kyc.step.get_started.step2.title')}
              description={t('e_invoice.kyc.step.get_started.step2.description')}
              illustration={EInvoicingCheckmark}
            />
            <EInvoicingGetStartedStep
              title={t('e_invoice.kyc.step.get_started.step3.title')}
              description={t('e_invoice.kyc.step.get_started.step3.description')}
              illustration={EInvoicingContract}
            />
          </Flex>
        </Flex>
        <EInvoicingFooter
          description={t('e_invoice.kyc.step.get_started.footer')}
          isEmptyStatePage={!!proceedToNextStep}
          link={E_INVOICING_SUPPORT_LINK}
          linkDescription={t('dashboard.banner.e_invoicing.link')}
        />
      </Styled.GetStartedContainer>
    </EInvoicingGenericStatus>
  )
}
